import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  getDevicePlaneCommandInsights,
  getDevicePlaneCommandSeries,
  getDevicePlaneRoutingMeta,
  getDevicePlaneStats,
} from '../api/devicePlane.js';
import { downloadCsv } from '../lib/csv.js';

const TICK = { fontSize: 11, fill: '#898781' };
const TOOLTIP_STYLE = {
  background: '#1a1a19',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  color: '#fff',
  fontSize: 12,
};
const LEGEND_STYLE = { fontSize: 12, color: '#c3c2b7' };

function HBar({ label, value, max, color }: { label: string; value: number; max: number; color: string }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <span className="font-mono text-xs text-ink2 w-40 shrink-0 truncate" title={label}>{label}</span>
      <span className="flex-1 h-3 bg-surface2 rounded overflow-hidden">
        <span className="block h-full rounded" style={{ width: `${max > 0 ? (value / max) * 100 : 0}%`, background: color }} />
      </span>
      <b className="w-10 text-right tabular-nums text-xs text-ink">{value}</b>
    </div>
  );
}

export function DevicePlaneCommands() {
  const [bucket, setBucket] = useState<'hour' | 'day'>('hour');

  const { data: series } = useQuery({
    queryKey: ['dp-cmd-series', bucket],
    queryFn: () => getDevicePlaneCommandSeries({ bucket }),
    refetchInterval: 30000,
  });
  const { data: stats } = useQuery({
    queryKey: ['dp-cmd-stats', bucket],
    queryFn: () => getDevicePlaneStats({ windowHours: bucket === 'hour' ? 24 : 720 }),
    refetchInterval: 60000,
  });
  const { data: insights } = useQuery({
    queryKey: ['dp-cmd-insights', bucket],
    queryFn: () => getDevicePlaneCommandInsights(bucket === 'hour' ? 24 : 720),
    refetchInterval: 60000,
  });
  const { data: routing } = useQuery({
    queryKey: ['dp-routing-meta'],
    queryFn: getDevicePlaneRoutingMeta,
    staleTime: Infinity,
  });

  const points = (series?.points ?? []).map((p) => ({
    t:
      bucket === 'hour'
        ? new Date(p.bucketStart).toLocaleTimeString([], { hour: '2-digit' })
        : new Date(p.bucketStart).toLocaleDateString([], { month: 'short', day: 'numeric' }),
    ok: p.ok,
    error: p.error,
    timeout: p.timeout,
    p50: p.p50LatencyMs,
    p95: p.p95LatencyMs,
  }));

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-ink">Device Plane — Commands</h1>
          <p className="text-sm text-muted mt-0.5">
            Command flow through the gateway: volume, outcomes and device response latency.
          </p>
        </div>
        <div className="flex gap-2">
          {(['hour', 'day'] as const).map((b) => (
            <button
              key={b}
              onClick={() => setBucket(b)}
              className={`px-3 py-1.5 rounded-lg text-sm border ${
                bucket === b
                  ? 'bg-accent text-white border-accent'
                  : 'bg-surface text-ink2 border-white/10 hover:bg-surface2'
              }`}
            >
              {b === 'hour' ? 'Last 24 h' : 'Last 30 d'}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-surface border border-white/10 rounded-xl p-4">
        <p className="text-sm font-semibold text-ink2 mb-2">Outcomes per {bucket}</p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={points} margin={{ top: 4, right: 8, bottom: 0, left: -14 }}>
              <XAxis dataKey="t" tick={TICK} interval="preserveStartEnd" stroke="#383835" />
              <YAxis tick={TICK} allowDecimals={false} stroke="#383835" />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Bar dataKey="ok" stackId="o" fill="#0ca30c" name="OK" />
              <Bar dataKey="error" stackId="o" fill="#d03b3b" name="Error" />
              <Bar dataKey="timeout" stackId="o" fill="#fab219" name="Timeout" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-surface border border-white/10 rounded-xl p-4">
        <p className="text-sm font-semibold text-ink2 mb-2">Ack latency (ms) — p50 / p95</p>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={points} margin={{ top: 4, right: 8, bottom: 0, left: -14 }}>
              <XAxis dataKey="t" tick={TICK} interval="preserveStartEnd" stroke="#383835" />
              <YAxis tick={TICK} stroke="#383835" />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Line type="monotone" dataKey="p50" stroke="#3987e5" dot={false} name="p50" />
              <Line type="monotone" dataKey="p95" stroke="#9085e9" dot={false} name="p95" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="bg-surface border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-ink2">By command type</p>
            <button
              onClick={() => downloadCsv('commands-by-type', stats?.byType ?? [])}
              className="text-xs text-accent hover:underline"
            >
              Export CSV
            </button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted border-b border-grid">
                <th className="py-1.5">Type</th>
                <th className="py-1.5 text-right">Count</th>
                <th className="py-1.5 text-right">Avg latency</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.byType ?? []).map((t) => (
                <tr key={t.type} className="border-b border-grid">
                  <td className="py-1.5 font-mono text-xs text-ink2">{t.type}</td>
                  <td className="py-1.5 text-right tabular-nums text-ink">{t.count}</td>
                  <td className="py-1.5 text-right tabular-nums text-muted">
                    {t.avgLatencyMs === null ? '—' : `${Math.round(t.avgLatencyMs)} ms`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-surface border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-ink2">Top devices by traffic</p>
            <button
              onClick={() => downloadCsv('commands-by-device', stats?.byDevice ?? [])}
              className="text-xs text-accent hover:underline"
            >
              Export CSV
            </button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted border-b border-grid">
                <th className="py-1.5">Device</th>
                <th className="py-1.5 text-right">Total</th>
                <th className="py-1.5 text-right">OK</th>
                <th className="py-1.5 text-right">Err</th>
                <th className="py-1.5 text-right">T/O</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.byDevice ?? [])
                .slice()
                .sort((a, b) => b.total - a.total)
                .slice(0, 12)
                .map((d) => (
                  <tr key={d.deviceId} className="border-b border-grid">
                    <td className="py-1.5 font-mono text-xs text-muted">{d.deviceId.slice(0, 8)}…</td>
                    <td className="py-1.5 text-right tabular-nums text-ink">{d.total}</td>
                    <td className="py-1.5 text-right tabular-nums text-goodink">{d.ok}</td>
                    <td className="py-1.5 text-right tabular-nums text-badink">{d.error}</td>
                    <td className="py-1.5 text-right tabular-nums text-warnink">{d.timeout}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Failure anatomy + attribution + routing knowledge */}
      <div className="grid md:grid-cols-2 gap-5">
        <div className="bg-surface border border-white/10 rounded-xl p-4">
          <p className="text-sm font-semibold text-ink2 mb-1">Error codes</p>
          <p className="text-xs text-muted mb-2">Device-reported errorCode on failed acks ({bucket === 'hour' ? 'last 24 h' : 'last 30 d'}).</p>
          {(insights?.errorCodes ?? []).map((e) => (
            <HBar key={e.code} label={e.code} value={e.count}
              max={Math.max(...(insights?.errorCodes ?? []).map((x) => x.count), 1)} color="#d03b3b" />
          ))}
          {insights && insights.errorCodes.length === 0 && (
            <p className="text-sm text-muted py-3 text-center">No errors in this window 🎉</p>
          )}
        </div>

        <div className="bg-surface border border-white/10 rounded-xl p-4">
          <p className="text-sm font-semibold text-ink2 mb-1">Timeout reasons</p>
          <p className="text-xs text-muted mb-2">ack_timeout = device online but silent · queue_expired = offline past TTL.</p>
          {(insights?.timeoutReasons ?? []).map((r) => (
            <HBar key={r.reason} label={r.reason} value={r.count}
              max={Math.max(...(insights?.timeoutReasons ?? []).map((x) => x.count), 1)} color="#fab219" />
          ))}
          {insights && insights.timeoutReasons.length === 0 && (
            <p className="text-sm text-muted py-3 text-center">No timeouts in this window 🎉</p>
          )}
        </div>

        <div className="bg-surface border border-white/10 rounded-xl p-4">
          <p className="text-sm font-semibold text-ink2 mb-1">Who is sending commands</p>
          <p className="text-xs text-muted mb-2">Issuer origin recorded on every settled command.</p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted border-b border-grid">
                <th className="py-1.5">Origin</th>
                <th className="py-1.5 text-right">Commands</th>
              </tr>
            </thead>
            <tbody>
              {(insights?.byOrigin ?? []).map((o) => (
                <tr key={o.origin} className="border-b border-grid">
                  <td className="py-1.5">
                    <span className="inline-block text-xs font-medium rounded-full px-2 py-0.5 bg-surface2 text-ink2">{o.origin}</span>
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-ink">{o.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-surface border border-white/10 rounded-xl p-4">
          <p className="text-sm font-semibold text-ink2 mb-1">Family matrix &amp; queue TTL</p>
          <p className="text-xs text-muted mb-2">Which command types each family accepts, and how long each waits for an offline device.</p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted border-b border-grid">
                <th className="py-1.5">Command</th>
                <th className="py-1.5 text-center">m8</th>
                <th className="py-1.5 text-center">cop-lop</th>
                <th className="py-1.5 text-right">TTL</th>
              </tr>
            </thead>
            <tbody>
              {(routing?.rows ?? []).map((r) => (
                <tr key={r.cmdType} className="border-b border-grid">
                  <td className="py-1.5 font-mono text-xs text-ink2">{r.cmdType}</td>
                  <td className={`py-1.5 text-center text-xs font-bold ${r.families.includes('m8') ? 'text-goodink' : 'text-muted'}`}>
                    {r.families.includes('m8') ? '✓' : '—'}
                  </td>
                  <td className={`py-1.5 text-center text-xs font-bold ${r.families.includes('cop-lop') ? 'text-goodink' : 'text-muted'}`}>
                    {r.families.includes('cop-lop') ? '✓' : '—'}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-xs text-muted">
                    {r.ttlSeconds >= 86400 ? `${Math.round(r.ttlSeconds / 86400)} d` : `${r.ttlSeconds} s`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
